/**
 * SitePageEditor — Full Squarespace-level CMS editor.
 *
 * Three-panel layout:
 *   Left  (380px) — tabbed: "Design" (template sections) | "Blocks" (palette + DnD canvas)
 *   Right (flex)  — responsive preview with Desktop/Tablet/Mobile toggle
 *
 * Features: drag-and-drop reorder, undo/redo (Ctrl+Z/Y), block add/clone/delete/toggle,
 * per-block config panel, auto-save indicator, responsive preview, keyboard shortcuts.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ArrowLeft, Save, ChevronDown, ChevronRight,
  Upload, Trash2, Plus, GripVertical, ExternalLink,
  Image as ImageIcon, Navigation, Undo2, Redo2, Settings2,
  Layers, Paintbrush, Search, Palette, X, type LucideIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../../services/api';
import { getTemplateOrDefault, type SiteSection, type SiteField } from './siteTemplates';
import SiteTemplateRenderer from './SiteTemplateRenderer';
import { useEditorHistory } from './useEditorHistory';
import { SortableBlockList } from './SortableBlockList';
import BlockConfigPanel from './BlockConfigPanel';
import BlockPalette from './BlockPalette';
import { PreviewToolbar, ResponsivePreview, type PreviewMode } from './ResponsivePreview';
import { getBlockEntry } from './componentRegistry';
import type { BlockInstance } from './types';
import PageSettingsPanel, { type PageSettingsPanelSettings } from './PageSettingsPanel';
import BrandStylesPanel, { type BrandStyles } from './BrandStylesPanel';
import { useAutoSave } from './useAutoSave';

/* ─── Types ──────────────────────────────────────────────────────── */

interface SitePageEditorProps {
  page: {
    id: string;
    title: string;
    slug: string;
    status: string;
    section_content: {
      blocks?: any[];
      settings?: any;
    };
    nav_visible?: boolean;
    nav_order?: number;
    section_visibility?: Record<string, any>;
  };
  onBack: () => void;
  onSaved: () => void;
}

/* ─── Image Uploader ──────────────────────────────────────────────── */

function ImageUploader({ value, onChange }: { value?: string; onChange: (url: string) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('images', file);
      const res: any = await api.post('/admin/page-templates/images/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res?.data?.data || res?.data || [];
      if (Array.isArray(data) && data.length > 0) {
        onChange(data[0].url);
      }
    } catch (err) {
      console.error('Image upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative group rounded-md overflow-hidden border border-border">
          <img src={value} alt="" className="w-full h-32 object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>
              <Upload size={14} className="mr-1" /> Replace
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onChange('')}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full h-24 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          disabled={uploading}
        >
          {uploading ? (
            <span className="text-sm">Uploading...</span>
          ) : (
            <>
              <ImageIcon size={20} className="mb-1" />
              <span className="text-xs">Click to upload image</span>
            </>
          )}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

/* ─── List Editors ────────────────────────────────────────────────── */

interface ListItem { [key: string]: string }

function FAQEditor({ items, onChange }: { items: Array<{ question: string; answer: string }>; onChange: (v: any[]) => void }) {
  const add = () => onChange([...items, { question: '', answer: '' }]);
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, field: string, val: string) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: val };
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="border border-border rounded-md p-3 space-y-2">
          <div className="flex items-start gap-2">
            <GripVertical size={14} className="mt-2 text-muted-foreground shrink-0" />
            <div className="flex-1 space-y-2">
              <input className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background" placeholder="Question" value={item.question} onChange={e => update(i, 'question', e.target.value)} />
              <textarea className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background resize-none" rows={2} placeholder="Answer" value={item.answer} onChange={e => update(i, 'answer', e.target.value)} />
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-destructive shrink-0"><Trash2 size={14} /></Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="w-full"><Plus size={14} className="mr-1" /> Add FAQ</Button>
    </div>
  );
}

function RichListEditor({ items, onChange, fields }: { items: any[]; onChange: (v: any[]) => void; fields: { key: string; label: string }[] }) {
  const add = () => {
    const empty: any = {};
    fields.forEach(f => { empty[f.key] = ''; });
    onChange([...items, empty]);
  };
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, key: string, val: string) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [key]: val };
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      {items.map((item: any, i: number) => (
        <div key={i} className="border border-border rounded-md p-3 space-y-2">
          <div className="flex items-start gap-2">
            <GripVertical size={14} className="mt-2 text-muted-foreground shrink-0" />
            <div className="flex-1 space-y-2">
              {fields.map(f => (
                <input key={f.key} className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background" placeholder={f.label} value={item[f.key] || ''} onChange={e => update(i, f.key, e.target.value)} />
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-destructive shrink-0"><Trash2 size={14} /></Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="w-full"><Plus size={14} className="mr-1" /> Add item</Button>
    </div>
  );
}

function StatsEditor({ items, onChange }: { items: Array<{ label: string; value: string }>; onChange: (v: any[]) => void }) {
  return <RichListEditor items={items} onChange={onChange} fields={[{ key: 'value', label: 'Value (e.g. 18%+)' }, { key: 'label', label: 'Label (e.g. target IRR)' }]} />;
}

function FeaturesEditor({ items, onChange }: { items: Array<{ title: string; description: string }>; onChange: (v: any[]) => void }) {
  return <RichListEditor items={items} onChange={onChange} fields={[{ key: 'title', label: 'Title' }, { key: 'description', label: 'Description' }]} />;
}

function TeamMembersEditor({ items, onChange }: { items: Array<{ name: string; role: string; bio?: string; image?: string }>; onChange: (v: any[]) => void }) {
  const add = () => onChange([...items, { name: '', role: '', bio: '', image: '' }]);
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i: number, key: string, val: string) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [key]: val };
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      {items.map((m: any, i: number) => (
        <div key={i} className="border border-border rounded-md p-3 space-y-2">
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <input className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background" placeholder="Name" value={m.name || ''} onChange={e => update(i, 'name', e.target.value)} />
              <input className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background" placeholder="Role / Title" value={m.role || ''} onChange={e => update(i, 'role', e.target.value)} />
              <textarea className="w-full px-3 py-1.5 text-sm border border-border rounded bg-background resize-none" rows={2} placeholder="Bio (optional)" value={m.bio || ''} onChange={e => update(i, 'bio', e.target.value)} />
              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Photo</label>
                <ImageUploader value={m.image} onChange={url => update(i, 'image', url)} />
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-destructive shrink-0"><Trash2 size={14} /></Button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="w-full"><Plus size={14} className="mr-1" /> Add team member</Button>
    </div>
  );
}

function NavLinksEditor({ items, onChange }: { items: Array<{ label: string; url: string }>; onChange: (v: any[]) => void }) {
  return <RichListEditor items={items} onChange={onChange} fields={[{ key: 'label', label: 'Link text' }, { key: 'url', label: 'URL (e.g. /about or /p/team)' }]} />;
}

/* ─── Main Editor ────────────────────────────────────────────────── */

type EditorTab = 'design' | 'blocks';

const SitePageEditor: React.FC<SitePageEditorProps> = ({ page, onBack, onSaved }) => {
  /* ── Template resolution ─────────────────────────────────────── */
  const SLUG_TO_TEMPLATE: Record<string, string> = {
    'home': 'landing',
    'hive-fund': 'hivefund',
    'about': 'about',
    'contact': 'contact',
  };
  const templateId = page.section_content?.settings?.siteTemplate
    || SLUG_TO_TEMPLATE[page.slug] || 'generic';
  const template = getTemplateOrDefault(templateId);

  /* ── Section data ────────────────────────────────────────────── */
  const initialSections = (): Record<string, any> => {
    const sc = page.section_content;
    const saved = sc?.settings?.sections || {};
    const merged: Record<string, any> = {};
    for (const sec of template.sections) {
      merged[sec.id] = { ...(template.defaults[sec.id] || {}), ...(saved[sec.id] || {}) };
    }
    return merged;
  };

  const [sections, setSections] = useState<Record<string, any>>(initialSections);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ hero: true });
  const [navVisible, setNavVisible] = useState(page.nav_visible || false);
  const [navOrder, setNavOrder] = useState(page.nav_order || 0);
  const [sectionVisibility, setSectionVisibility] = useState<Record<string, any>>(page.section_visibility || {});

  /* ── UI state ────────────────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState<EditorTab>('design');
  const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [blockPaletteOpen, setBlockPaletteOpen] = useState(false);
  const [designSearch, setDesignSearch] = useState('');

  /* ── Brand styles ────────────────────────────────────────────── */
  const [brandPanelOpen, setBrandPanelOpen] = useState(false);
  const [brandStyles, setBrandStyles] = useState<BrandStyles>(
    (page.section_content?.settings?.brandStyles as BrandStyles) ?? {}
  );

  /* ── Page Settings ───────────────────────────────────────────── */
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [pageSettings, setPageSettings] = useState<PageSettingsPanelSettings>({
    title: page.title || '',
    slug: page.slug || '',
    description: page.section_content?.settings?.description || '',
    ogImage: page.section_content?.settings?.ogImage || '',
    layout: page.section_content?.settings?.layout || 'default',
    maxWidth: page.section_content?.settings?.maxWidth || 'xl',
    spacing: page.section_content?.settings?.spacing || 'normal',
    entityType: page.section_content?.settings?.entityType ?? null,
    entityId: page.section_content?.settings?.entityId || '',
    status: (page.status as 'draft' | 'published') || 'draft',
  });
  const handleUpdateSettings = useCallback((partial: Partial<PageSettingsPanelSettings>) => {
    setPageSettings(prev => ({ ...prev, ...partial }));
  }, []);

  /* ── Block canvas — undo/redo + DnD ─────────────────────────── */
  const initialBlocks: BlockInstance[] = Array.isArray(page.section_content?.blocks)
    ? (page.section_content.blocks as BlockInstance[])
    : [];

  const { blocks, setBlocks, undo, redo, canUndo, canRedo } = useEditorHistory(initialBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;

  /* ── Block handlers ──────────────────────────────────────────── */
  const handleAddBlock = useCallback((blockKey: string) => {
    const entry = getBlockEntry(blockKey);
    if (!entry) return;
    const newBlock: BlockInstance = {
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      blockKey,
      props: { ...entry.defaultProps },
      visible: true,
      order: blocks.length,
    };
    setBlocks([...blocks, newBlock], true);
    setSelectedBlockId(newBlock.id);
    setBlockPaletteOpen(false);
  }, [blocks, setBlocks]);

  const handleDeleteBlock = useCallback((blockId: string) => {
    setBlocks(blocks.filter(b => b.id !== blockId), true);
    setSelectedBlockId(prev => (prev === blockId ? null : prev));
  }, [blocks, setBlocks]);

  const handleCloneBlock = useCallback((blockId: string) => {
    const source = blocks.find(b => b.id === blockId);
    if (!source) return;
    const clone: BlockInstance = {
      ...source,
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      order: source.order + 0.5,
      props: { ...source.props },
    };
    const reordered = [...blocks, clone]
      .sort((a, b) => a.order - b.order)
      .map((b, i) => ({ ...b, order: i }));
    setBlocks(reordered, true);
  }, [blocks, setBlocks]);

  const handleToggleVisible = useCallback((blockId: string) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, visible: !b.visible } : b), true);
  }, [blocks, setBlocks]);

  const handleBlockPropsUpdate = useCallback((blockId: string, props: Record<string, unknown>) => {
    const { __layoutHint, ...rest } = props as Record<string, unknown> & { __layoutHint?: string };
    setBlocks(
      blocks.map(b =>
        b.id === blockId
          ? {
              ...b,
              props: rest as Record<string, any>,
              ...(__layoutHint !== undefined ? { layoutHint: __layoutHint as BlockInstance['layoutHint'] } : {}),
            }
          : b,
      ),
      false, // live typing — no history branch per keystroke
    );
  }, [blocks, setBlocks]);

  /* ── Keyboard shortcuts ──────────────────────────────────────── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  /* ── Field helpers ───────────────────────────────────────────── */
  const toggle = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const updateField = useCallback((sectionId: string, key: string, value: any) => {
    setSections(prev => ({ ...prev, [sectionId]: { ...(prev[sectionId] || {}), [key]: value } }));
  }, []);
  const getVal = (sectionId: string, key: string): any => sections[sectionId]?.[key] ?? '';

  /* ── Save ────────────────────────────────────────────────────── */
  const handleSave = async (publish = false) => {
    if (publish) setPublishing(true); else setSaving(true);
    setSaveMsg('');
    try {
      await api.put(`/admin/page-templates/components/instances/${page.id}`, {
        title: page.title,
        blocks,           // ← live block canvas state
        settings: {
          ...(page.section_content?.settings || {}),
          siteTemplate: templateId,
          sections,
          ...pageSettings,
          brandStyles,
        },
        navVisible,
        navOrder,
        sectionVisibility,
        ...(publish ? { status: 'published' } : {}),
      });
      setSaveMsg(publish ? 'Published!' : 'Saved!');
      setTimeout(() => setSaveMsg(''), 2500);
      onSaved();
    } catch {
      setSaveMsg('Save failed');
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  /* ── Auto-save ───────────────────────────────────────────────── */
  const autoSaveData = { blocks, sections, navVisible, navOrder, sectionVisibility };
  const { isSaving: autoSaving, lastSaved, isDirty } = useAutoSave({
    data: autoSaveData,
    onSave: () => handleSave(false),
    delay: 4000,
  });

  /* ── Field renderer (Design tab) ─────────────────────────────── */
  const renderField = (sectionId: string, field: SiteField) => {
    const val = getVal(sectionId, field.key);
    switch (field.type) {
      case 'image':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <ImageUploader value={val || ''} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'textarea':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <textarea
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              rows={3}
              placeholder={field.placeholder}
              value={val || ''}
              onChange={e => updateField(sectionId, field.key, e.target.value)}
            />
          </div>
        );
      case 'faq':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <FAQEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'richlist':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <FeaturesEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'stats':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <StatsEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'features':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <FeaturesEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'teamMembers':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <TeamMembersEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      case 'links':
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <NavLinksEditor items={Array.isArray(val) ? val : []} onChange={v => updateField(sectionId, field.key, v)} />
          </div>
        );
      default:
        return (
          <div key={field.key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
            <input
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              type={field.type === 'url' ? 'url' : 'text'}
              placeholder={field.placeholder}
              value={val || ''}
              onChange={e => updateField(sectionId, field.key, e.target.value)}
            />
          </div>
        );
    }
  };

  /* ── Design search helper ────────────────────────────────────── */
  const sectionMatchesSearch = (sec: SiteSection, q: string): boolean => {
    const lq = q.toLowerCase();
    return sec.label.toLowerCase().includes(lq) || sec.fields.some(f => f.label.toLowerCase().includes(lq));
  };

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className="h-full flex flex-col bg-background">

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-background shrink-0">
        <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0">
          <ArrowLeft size={15} className="mr-1" /> Back
        </Button>

        <div className="min-w-0 mr-2">
          <p className="text-sm font-semibold truncate leading-none">{page.title}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[11px] text-muted-foreground">/{page.slug}</span>
            <Badge variant="outline" className="text-[10px] py-0 px-1">{template.label}</Badge>
            {page.status === 'published' && <Badge className="text-[10px] py-0 px-1 bg-green-500">Live</Badge>}
          </div>
        </div>

        {/* Undo / Redo */}
        <div className="flex items-center gap-0.5 border border-border rounded-md p-0.5 shrink-0">
          <Button
            variant="ghost" size="sm"
            onClick={undo} disabled={!canUndo}
            className="h-7 w-7 p-0"
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={13} />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={redo} disabled={!canRedo}
            className="h-7 w-7 p-0"
            title="Redo (Ctrl+Y)"
          >
            <Redo2 size={13} />
          </Button>
        </div>

        {/* Responsive preview toggle — centre of toolbar */}
        <div className="flex-1 flex justify-center">
          <PreviewToolbar mode={previewMode} onChange={setPreviewMode} />
        </div>

        <Button variant="outline" size="sm" onClick={() => setBrandPanelOpen(true)} className="shrink-0">
          <Palette size={13} className="mr-1" /> Brand
        </Button>
        <Button variant="outline" size="sm" onClick={() => setSettingsPanelOpen(true)} className="shrink-0">
          <Settings2 size={13} className="mr-1" /> Settings
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving || autoSaving} className="shrink-0">
          <Save size={13} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
        <div className="relative shrink-0">
          <Button
            size="sm"
            onClick={() => handleSave(true)}
            disabled={publishing}
            className="bg-primary hover:bg-primary/90 text-primary-foreground pr-8"
          >
            <ExternalLink size={13} className="mr-1" />
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 text-[11px] text-muted-foreground min-w-[90px]">
          {autoSaving || saving ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" /> Saving…</>
          ) : saveMsg ? (
            <><span className={`w-1.5 h-1.5 rounded-full inline-block ${saveMsg.includes('fail') ? 'bg-red-400' : 'bg-green-400'}`} /> {saveMsg}</>
          ) : lastSaved ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
          ) : isDirty ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> Unsaved changes</>
          ) : null}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left Panel ──────────────────────────────────────── */}
        <div className="w-[380px] shrink-0 border-r border-border flex flex-col bg-background">

          {/* Tab switcher */}
          <div className="flex border-b border-border shrink-0">
            <button
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                activeTab === 'design'
                  ? 'text-foreground border-b-2 border-primary bg-primary/10'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab('design')}
            >
              <Paintbrush size={13} /> Design
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                activeTab === 'blocks'
                  ? 'text-foreground border-b-2 border-primary bg-primary/10'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => { setActiveTab('blocks'); setSelectedBlockId(null); setBlockPaletteOpen(false); }}
            >
              <Layers size={13} /> Blocks
              {blocks.length > 0 && (
                <span className="ml-1 bg-muted text-muted-foreground rounded-full text-[10px] px-1.5 py-0 leading-4">
                  {blocks.length}
                </span>
              )}
            </button>
          </div>

          {/* ── DESIGN TAB ─────────────────────────────────────── */}
          {activeTab === 'design' && (
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 pb-0">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Search sections and fields…"
                    value={designSearch}
                    onChange={e => setDesignSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="p-3 space-y-2">
                {/* Navigation */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => toggle('_nav')}
                  >
                    <Navigation size={14} className="text-muted-foreground shrink-0" />
                    <span className="flex-1 text-xs font-medium">Navigation</span>
                    {navVisible && <Badge className="text-[9px] py-0 px-1">In Nav</Badge>}
                    {expanded['_nav'] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                  {expanded['_nav'] && (
                    <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={navVisible} onChange={e => setNavVisible(e.target.checked)} className="rounded border-border" />
                        <span className="text-xs">Show in navigation bar</span>
                      </label>
                      <div className="space-y-1">
                        <label className="text-[11px] font-medium text-muted-foreground">Nav order</label>
                        <input
                          type="number"
                          className="w-20 px-2 py-1 text-xs border border-border rounded-md bg-background"
                          value={navOrder}
                          onChange={e => setNavOrder(parseInt(e.target.value) || 0)}
                        />
                      </div>
                      {page.slug === 'home' && (
                        <div className="space-y-2 pt-2 border-t border-border">
                          <p className="text-[11px] font-medium text-muted-foreground">Section nav visibility</p>
                          {template.sections
                            .filter(s => !['nav', 'footer', 'newsletter', 'hero'].includes(s.id))
                            .map(sec => {
                              const cfg = sectionVisibility[sec.id];
                              const isObj = typeof cfg === 'object' && cfg !== null;
                              const visible = isObj ? cfg.visible : !!cfg;
                              const label = (isObj && cfg.label) ? cfg.label : sec.label;
                              const order = (isObj && typeof cfg.order === 'number') ? cfg.order : 99;
                              return (
                                <div key={sec.id} className="flex items-center gap-2">
                                  <input
                                    type="checkbox" checked={visible}
                                    onChange={e => setSectionVisibility(prev => ({ ...prev, [sec.id]: { visible: e.target.checked, label, order } }))}
                                    className="rounded border-border"
                                  />
                                  <input
                                    className="flex-1 px-2 py-1 text-[11px] border border-border rounded bg-background"
                                    value={label}
                                    onChange={e => setSectionVisibility(prev => ({
                                      ...prev,
                                      [sec.id]: { ...((isObj && cfg) || { visible, order }), label: e.target.value },
                                    }))}
                                    placeholder={sec.label}
                                  />
                                  <input
                                    type="number"
                                    className="w-12 px-2 py-1 text-[11px] border border-border rounded bg-background"
                                    value={order}
                                    onChange={e => setSectionVisibility(prev => ({
                                      ...prev,
                                      [sec.id]: { ...((isObj && cfg) || { visible, label }), order: parseInt(e.target.value) || 0 },
                                    }))}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {template.sections
                  .filter((sec: SiteSection) => !designSearch || sectionMatchesSearch(sec, designSearch))
                  .map((sec: SiteSection) => {
                  const Icon: LucideIcon = sec.icon;
                  const isExpanded = expanded[sec.id] || (!!designSearch && sectionMatchesSearch(sec, designSearch));
                  return (
                    <div key={sec.id} className="border border-border rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                        onClick={() => toggle(sec.id)}
                      >
                        <Icon size={14} className="text-muted-foreground shrink-0" />
                        <span className="flex-1 text-xs font-medium">{sec.label}</span>
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
                          {sec.fields.map(f => renderField(sec.id, f))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── BLOCKS TAB ─────────────────────────────────────── */}
          {activeTab === 'blocks' && (
            <div className="flex-1 flex flex-col overflow-hidden">

              {blockPaletteOpen ? (
                /* Block palette — add new block */
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setBlockPaletteOpen(false)}>
                      <ArrowLeft size={12} className="mr-1" /> Cancel
                    </Button>
                    <span className="text-xs font-medium">Add a Block</span>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <BlockPalette
                      onAddBlock={handleAddBlock}
                      existingBlocks={blocks}
                    />
                  </div>
                </div>
              ) : (
                /* Split panel: block list (top) + config pinned at bottom when selected */
                <>
                  {/* Block list — always visible */}
                  <div className={`flex flex-col overflow-hidden border-b border-border ${selectedBlock ? 'h-[45%]' : 'flex-1'}`}>
                    {blocks.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-muted-foreground">
                        <Layers size={32} className="mb-3 opacity-30" />
                        <p className="text-sm font-medium mb-1">No blocks yet</p>
                        <p className="text-xs mb-4">Add blocks to build custom page sections below your template content.</p>
                        <Button size="sm" onClick={() => setBlockPaletteOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                          <Plus size={14} className="mr-1" /> Add First Block
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-y-auto">
                          <SortableBlockList
                            blocks={blocks}
                            selectedBlockId={selectedBlockId}
                            isEditing
                            onSelect={setSelectedBlockId}
                            onReorder={(reordered) => setBlocks(reordered, true)}
                            onDelete={handleDeleteBlock}
                            onClone={handleCloneBlock}
                            onToggleVisible={handleToggleVisible}
                            onInsertAfter={(afterIndex) => {
                              setBlockPaletteOpen(true);
                              // store insert position for next add
                              (window as any).__insertAfterIndex = afterIndex;
                            }}
                            renderBlock={(block) => {
                              const entry = getBlockEntry(block.blockKey);
                              return (
                                <div className={`px-3 py-2 transition-colors ${block.id === selectedBlockId ? 'bg-primary/5' : ''}`}>
                                  <p className="text-xs font-medium truncate">{entry?.label ?? block.blockKey}</p>
                                  {entry?.description && (
                                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{entry.description}</p>
                                  )}
                                </div>
                              );
                            }}
                          />
                        </div>
                        <div className="p-2 border-t border-border shrink-0">
                          <Button
                            size="sm" variant="outline"
                            className="w-full border-dashed hover:border-primary hover:bg-primary/10 h-7 text-xs"
                            onClick={() => setBlockPaletteOpen(true)}
                          >
                            <Plus size={12} className="mr-1" /> Add Block
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Config panel — pinned at bottom when block selected */}
                  {selectedBlock && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border shrink-0 bg-muted/20">
                        <span className="text-[11px] font-semibold flex-1 truncate">
                          {getBlockEntry(selectedBlock.blockKey)?.label ?? selectedBlock.blockKey}
                        </span>
                        <button
                          onClick={() => setSelectedBlockId(null)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <X size={13} />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <BlockConfigPanel
                          block={selectedBlock}
                          onUpdate={handleBlockPropsUpdate}
                          onDelete={(id) => { handleDeleteBlock(id); setSelectedBlockId(null); }}
                          onClose={() => setSelectedBlockId(null)}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Right Panel — Responsive Preview ──────────────────── */}
        <div className="flex-1 overflow-hidden bg-muted/20">
          <ResponsivePreview mode={previewMode}>
            <div className="bg-background min-h-full">
              <SiteTemplateRenderer
                templateId={templateId}
                sections={sections}
                blocks={blocks}
                blockSettings={{ ...pageSettings, brandStyles }}
                isPreview
                isEditing
                onSelectBlock={(id) => {
                  setActiveTab('blocks');
                  setSelectedBlockId(id);
                  setBlockPaletteOpen(false);
                }}
                selectedBlockId={selectedBlockId}
              />
            </div>
          </ResponsivePreview>
        </div>
      </div>

      {/* Page Settings Sheet */}
      <PageSettingsPanel
        isOpen={settingsPanelOpen}
        settings={pageSettings}
        onClose={() => setSettingsPanelOpen(false)}
        onUpdate={handleUpdateSettings}
      />

      {/* Brand Styles Sheet */}
      <BrandStylesPanel
        isOpen={brandPanelOpen}
        value={brandStyles}
        onClose={() => setBrandPanelOpen(false)}
        onChange={setBrandStyles}
      />
    </div>
  );
};

export default SitePageEditor;
